import { Box, CircularProgress, Typography } from '@material-ui/core';
import { Layout, LoginLogoImg, useBaseStore } from '@roc/feature-app-core';
import { createPublicLinkClient } from '@roc-capital/public-link';
import { usePublicLinkSession } from '@roc-capital/public-link/react';
import { Button } from '@roc/ui';
import { observer } from 'mobx-react';
import { useLayoutEffect, useMemo } from 'react';
import { usePublicLinkAchFormStore } from '../hooks/usePublicLinkAchFormStore';
import { ACHFormPageComponent } from './achFormPage';

const Message = ({ title, text, action }: { title: string; text: string; action?: { label: string; onClick: () => void } }) => (
  <Box textAlign="center" py={6} px={2} maxWidth={520} mx="auto">
    <Typography variant="h5" gutterBottom>
      {title}
    </Typography>
    <Typography color="textSecondary">{text}</Typography>
    {action && (
      <Box mt={3}>
        <Button testId="retry" variant="outlined" color="primary" onClick={action.onClick}>
          {action.label}
        </Button>
      </Box>
    )}
  </Box>
);

/**
 * The ACH consent form for public links: /link/ach. The borrower arrives here from the platform landing page
 * (/platform-app/link#token), which started the session; every call is authorized by that session, and the loan and
 * borrower come from it on the server. Replaces /ach/:token, which carried an HMAC token in the URL.
 */
export const PublicLinkACHFormPage = observer(() => {
  const { environmentStore } = useBaseStore();
  const client = useMemo(
    () => createPublicLinkClient({ apiBaseUrl: environmentStore.environmentVariables.restApiBaseURL ?? '' }),
    []
  );
  const { status, request, retry } = usePublicLinkSession(client, { purpose: 'ACH_FORM' });
  const { achFormStore } = usePublicLinkAchFormStore();

  // A layout effect, so it runs before the ACH screens' own effects (the first data fetch) on the same commit.
  useLayoutEffect(() => achFormStore.setRequest(request), [achFormStore, request]);

  return (
    <Layout maxWidth={'lg'}>
      <Box style={{ display: 'flex', justifyContent: 'center' }}>
        <LoginLogoImg imgStyle={{ width: '150px' }} />
      </Box>
      {status === 'loading' && (
        <Box display="flex" justifyContent="center" py={6}>
          <CircularProgress />
        </Box>
      )}
      {status === 'ended' && (
        <Message
          title="Your session has ended"
          text="For your security, sessions end after 15 minutes or when the link is opened in another window. Open the link from your email again to continue."
        />
      )}
      {status === 'unavailable' && (
        <Message
          title="We couldn't open your form"
          text="This is usually temporary. Check your connection and try again."
          action={{ label: 'Try again', onClick: retry }}
        />
      )}
      {status === 'active' && (
        <ACHFormPageComponent encodedInput="public-link-session" />
      )}
    </Layout>
  );
});

/**
 * /ach/:token, the old ACH link format (an HMAC token in the URL). Those links no longer work: the lender sends a new
 * one, which opens /link/ach.
 */
export const AchLinkReplacedPage = () => (
  <Layout maxWidth={'lg'}>
    <Box style={{ display: 'flex', justifyContent: 'center' }}>
      <LoginLogoImg imgStyle={{ width: '150px' }} />
    </Box>
    <Message
      title="This link is no longer valid"
      text="Please ask your lender to send you a new link to complete your ACH form."
    />
  </Layout>
);
